/**
 * definitions of themes
 */

import {
  getCssVarObject,
  getFilterShadowObject,
  prefixObjectKeys,
} from '../utils';

const themeColorsDefault = {
  /**
   * Brand
   * brandMain: #17A191
   * brandMedium: #5BD3C1
   * brandLight: #B8F1E3
   * brandSuperLight: #67E0CA4D (30% alpha)
   */
  brandMain: '#17A191',
  brandMedium: '#5BD3C1',
  brandLight: '#B8F1E3',
  brandSuperLight: '#67E0CA4D', // 30% alpha

  /**
   * Background
   * backgroundBase: #F3F5F7
   * backgroundAlt: #FFFFFF
   * backgroundAltTransparent: #FFFFFF00
   * backgroundAltSolid: #FFFFFF
   * backgroundGradientHalf: radial-gradient(113.87% 115.79% at 27.66% -52.37%,#5affcdcc 0%,#5affcd00 100%) ,radial-gradient(110.76% 113.16% at 73.95% -47.19%,#5753ff6e 0%,#5753ff00 100%)
   * backgroundGradientFull: linear-gradient(311.66deg, rgba(65, 72, 226, 0.2) -13.98%, rgba(65, 72, 226, 0) 42.49%), radial-gradient(94.22% 116.41% at 36.4% 82.85%, rgba(15, 111, 255, 0.2) 0%, rgba(15, 111, 255, 0) 100%) , radial-gradient(135.79% 111.32% at 64.05% -16.67%, rgba(100, 255, 209, 0.9) 0%, rgba(81, 255, 224, 0) 100%) , radial-gradient(86.55% 88.86% at 84.74% -26.75%, rgba(15, 255, 241, 0.8) 0%, rgba(15, 169, 255, 0) 100%) , #191B1E
   */
  backgroundBase: '#F3F5F7',
  backgroundAlt: '#FFFFFF',
  backgroundAltTransparent: '#FFFFFF00', // 0% alpha, used for better transitions
  backgroundAltSolid: '#FFFFFF',
  backgroundGradientHalf:
    'radial-gradient(113.87% 115.79% at 27.66% -52.37%,#5affcdcc 0%,#5affcd00 100%) ,radial-gradient(110.76% 113.16% at 73.95% -47.19%,#5753ff6e 0%,#5753ff00 100%)',
  backgroundGradientFull:
    'linear-gradient(311.66deg, rgba(65, 72, 226, 0.2) -13.98%, rgba(65, 72, 226, 0) 42.49%), radial-gradient(94.22% 116.41% at 36.4% 82.85%, rgba(15, 111, 255, 0.2) 0%, rgba(15, 111, 255, 0) 100%) , radial-gradient(135.79% 111.32% at 64.05% -16.67%, rgba(100, 255, 209, 0.9) 0%, rgba(81, 255, 224, 0) 100%) , radial-gradient(86.55% 88.86% at 84.74% -26.75%, rgba(15, 255, 241, 0.8) 0%, rgba(15, 169, 255, 0) 100%) , #191B1E',

  /**
   * Ink
   * inkBase: #21242A
   * inkMain: #FFFFFF
   * inkMedium: #6F7780
   * inkLightMedium: #ADB4BE
   * inkLight: #E2E6ED
   * inkSuperLight: #F3F5F7
   */
  inkBase: '#FFFFFF',
  inkMain: '#21242A',
  inkMedium: '#6F7780',
  inkLightMedium: '#ADB4BE',
  inkLight: '#E2E6ED',
  inkSuperLight: '#F3F5F7',

  /**
   * Misc
   * highlight: #4148E2
   * selectionBlue: #18A0FB
   * selectionBlue10: #18A0FB1A
   * selectionBlue30: #18A0FB4D
   * alertRed: #FF5757
   * goldGradientLight: linear-gradient(180deg, #FFFFFD 0%, #FFECBC 100%)
   * goldGradient: linear-gradient(180deg, #F9C642 40.1%, #EBA429 100%)
   * bronze: #E77F5E
   * transparentBlack: #0000000D (5% alpha)
   * cropMask: #8585854d (30% alpha)
   */
  highlight: '#4148E2',
  selectionBlue: '#18A0FB',
  blueMain: '#18A0FB',
  blueMedium: '#18A0FB',
  blueLight: '#18A0FB',
  blueLight30: '#18A0FB4D',
  selectionBlue10: '#18A0FB1A',
  selectionBlue30: '#18A0FB4D',
  alertRed: '#FF5757',
  redMain: '#FF5757',
  redMedium: '#FF5757',
  redLight: '#FF5757',
  alertRedLight: '#FF575733', // 20% alpha
  goldGradientLight: 'linear-gradient(180deg, #FFFFFD 0%, #FFECBC 100%)',
  goldGradient: 'linear-gradient(180deg, #F9C642 40.1%, #EBA429 100%)',
  bronze: '#E77F5E',
  transparentBlack: '#0000000D', // 5% alpha
  cropMask: '#8585854d',

  transparent: 'transparent',

  greenMain: '#53C659',
  transparentMain5: '#0000000D',
  transparentMain10: '#0000001A',
  transparentMain15: '#00000026',
} as const;

const themeColorsDark = {
  /**
   * Brand
   * brandMain: #55D4C1
   * brandMedium: #89E7D9
   * brandLight: #C7FCEF
   * brandSuperLight: #67E0CA2E (18% alpha)
   */
  brandMain: '#55D4C1',
  brandMedium: '#89E7D9',
  brandLight: '#C7FCEF',
  brandSuperLight: '#67E0CA2E', // 18% alpha

  /**
   * Background
   * backgroundBase: #191B1E
   * backgroundAlt: #2B2F38
   * backgroundAltTransparent: #25282F00 (0% alpha, used for better transitions)
   * backgroundAltSolid: #363940
   */
  backgroundBase: '#191B1E',
  backgroundAlt: '#2B2F38',
  backgroundAltTransparent: '#25282F00', // 0% alpha, used for better transitions
  backgroundAltSolid: '#363940',

  /**
   * Ink
   * inkBase: #191B1E
   * inkMain: #E9EEF5
   * inkMedium: #7E8B98
   * inkLightMedium: #9EABBE80 (50% alpha)
   * inkLight: #686F8480 (50% alpha)
   * inkSuperLight: #3F455480 (50% alpha)
   */
  inkBase: '#191B1E',
  inkMain: '#E9EEF5',
  inkMedium: '#7E8B98',
  inkLightMedium: '#9EABBE80',
  inkLight: '#686F8480', // 50% alpha
  inkSuperLight: '#3F455480', // 50% alpha

  /**
   * Misc
   * highlight: #4E55FA
   * transparentBlack: #00000026 (15% alpha)
   */
  highlight: '#4E55FA',
  transparentBlack: '#00000026', // 15% alpha

  redMain: '#FF5757',
  redMedium: '#FF5757',
  redLight: '#FF5757',

  blueMain: '#18A0FB',
  blueMedium: '#18A0FB',
  blueLight: '#18A0FB',
  blueLight30: '#18A0FB4D',

  greenMain: '#53C659',
  transparentMain5: '#FFFFFF0D',
  transparentMain10: '#FFFFFF1A',
  transparentMain15: '#FFFFFF26',
} as const;

const themeShadowsDefault = {
  /**
   * Use over dark overlays
   * Use for Tooltips
   */
  shadowHeavy:
    '0px 60px 90px rgba(0, 0, 0, 0.18), 0px 12px 20px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.08)',
  /**
   * Use as default
   * Use for Menus
   */
  shadowMedium:
    '0px 36px 98px 0px rgba(0, 0, 0, 0.07), 0px 13px 36px 0px rgba(0, 0, 0, 0.05), 0px 6px 18px 0px rgba(0, 0, 0, 0.04), 0px 3px 8px 0px rgba(0, 0, 0, 0.03), 0px 2px 4px 0px rgba(0, 0, 0, 0.02)',

  /**
   * Use for modals that are integrated on a page
   * Ex. challenge tiles
   */
  shadowSoft:
    '0px 12px 32px rgba(0, 0, 0, 0.05), 0px 6px 15px rgba(0, 0, 0, 0.0389404), 0px 3px 7px rgba(0, 0, 0, 0.0310596), 0px 1px 3px rgba(0, 0, 0, 0.02)',
  /**
   * Use for active states, ex. inputs
   */
  shadowSmallFocus: '0px 1px 4px rgba(0, 0, 0, 0.14)',
  /**
   * Use for small elements that need hard contrast
   * Use for Color Swatches
   */
  shadowSmallHeavy: '1px 1px 3px rgba(0, 0, 0, 0.25)',
};

const themeFiltersDefault = getFilterShadowObject(themeShadowsDefault);

const themeShadowsDark = {
  /**
   * Use over dark overlays
   * Use for Tooltips
   * shadowHeavy: 0px 57px 91px rgba(0, 0, 0, 0.38), 0px 13px 20px rgba(0, 0, 0, 0.2), 0px 4px 6px rgba(0, 0, 0, 0.18)
   */
  shadowHeavy:
    '0px 57px 91px rgba(0, 0, 0, 0.38), 0px 13px 20px rgba(0, 0, 0, 0.2), 0px 4px 6px rgba(0, 0, 0, 0.18)',
  /**
   * Use as default
   * Use for Menus
   * shadowMedium: 0px 36px 98px rgba(0, 0, 0, 0.17), 0px 13px 36px rgba(0, 0, 0, 0.2), 0px 7px 18px rgba(0, 0, 0, 0.14), 0px 3px 9px rgba(0, 0, 0, 0.12), 0px 1px 4px rgba(0, 0, 0, 0.1217)
   */
  shadowMedium:
    '0px 36px 98px rgba(0, 0, 0, 0.17), 0px 13px 36px rgba(0, 0, 0, 0.2), 0px 7px 18px rgba(0, 0, 0, 0.14), 0px 3px 9px rgba(0, 0, 0, 0.12), 0px 1px 4px rgba(0, 0, 0, 0.1217)',
  /**
   * Use for modals that are integrated on a page
   * Ex. challenge tiles
   * shadowSoft: 0px 12px 30px rgba(0, 0, 0, 0.14), 0px 5px 25px rgba(0, 0, 0, 0.2), 0px 3px 8px rgba(0, 0, 0, 0.35), 0px 1px 3px rgba(0, 0, 0, 0.02)
   */
  shadowSoft:
    '0px 12px 30px rgba(0, 0, 0, 0.14), 0px 5px 25px rgba(0, 0, 0, 0.2), 0px 3px 8px rgba(0, 0, 0, 0.35), 0px 1px 3px rgba(0, 0, 0, 0.02)',
  /**
   * Use for active states, ex. inputs
   * shadowSmallFocus: 0px 1px 4px rgba(0, 0, 0, 0.45)
   */
  shadowSmallFocus: '0px 1px 4px rgba(0, 0, 0, 0.45)',
  /**
   * Use for small elements that need hard contrast
   * Use for Color Swatches
   * shadowSmallHeavy: 1px 1px 3px rgba(0, 0, 0, 0.55)
   */
  shadowSmallHeavy: '1px 1px 3px rgba(0, 0, 0, 0.55)',
} as const;

const themeFiltersDark = getFilterShadowObject(themeShadowsDark);

export const defaultTheme = {
  ...themeColorsDefault,
  ...themeShadowsDefault,
  ...themeFiltersDefault,
};

export const darkTheme = {
  ...themeColorsDark,
  ...themeShadowsDark,
  ...themeFiltersDark,
};

const prefixedDarkTheme = {
  ...prefixObjectKeys<typeof themeColorsDark, 'dark'>(themeColorsDark, 'dark'),
  ...prefixObjectKeys<typeof themeShadowsDark, 'dark'>(
    themeShadowsDark,
    'dark'
  ),
  ...prefixObjectKeys<typeof themeFiltersDark, 'dark'>(
    themeFiltersDark,
    'dark'
  ),
};

export const themeStyle = {
  ...defaultTheme,
  ...prefixedDarkTheme,

  /**
   * Font-sizes (from 10px to 36px)
   */
  fontSize10: '10px',
  fontSize11: '11px',
  fontSize12: '12px',
  fontSize14: '14px',
  fontSize16: '16px',
  fontSize18: '18px',
  fontSize26: '26px',
  fontSize36: '36px',

  /**
   * Font-weights (from 400 to 800)
   */
  fontRegular: 400,
  fontMedium: 500,
  fontSemiBold: 600,
  fontBold: 700,
  fontExtraBold: 800,

  /**
   * Border-radius
   * radiusSmall: 5px
   * radiusMedium: 8px
   * radiusLarge: 12px
   */
  radiusSmall: '5px',
  radiusMedium: '8px',
  radiusLarge: '12px',

  /**
   * z-indexes
   * zIndexNegative: -1
   * zIndexZero: 0
   * zIndexS1: 1
   * zIndexS2: 2
   * zIndexS3: 3
   * zIndexS9: 9
   * zIndexM1: 10
   * zIndexM9: 99
   * zIndexL9: 999
   */
  zIndexNegative: -1,
  zIndexZero: 0,
  zIndexS1: 1,
  zIndexS2: 2,
  zIndexS3: 3,
  zIndexS9: 9,
  zIndexM1: 10,
  zIndexM9: 99,
  zIndexL1: 100,
  zIndexL9: 999,

  /**
   * Transitions
   * transitionDuration: 0.3s
   */
  transitionDuration: '0.3s',

  // vars (colors, shadows)
  // using vars should be preferred over using theme colors directly
  ...getCssVarObject<typeof defaultTheme>(defaultTheme),
} as const;
